import React, { useEffect, useState } from 'react';
import { useProductsViewModel } from '../viewmodels/useProductsViewModel';
import { useCategoriesViewModel } from '../viewmodels/useCategoriesViewModel';
import { useTheme } from '../themes/ThemeProvider';
import { cn } from '../utils/cn';
import ProductGrid from '../components/product/ProductGrid';
import Icon from '../components/ui/Icon';
import CredentialsBar from '../components/brand/CredentialsBar';
import ClientLogoStrip from '../components/brand/ClientLogoStrip';
import IndustryTabs from '../components/brand/IndustryTabs';
import FullBleedSection from '../components/ui/FullBleedSection';
import { useLocalizedTo } from '../hooks/useLocaleNavigation';
import { isInternalPath } from '../i18n/localeRouting';

/**
 * Reads current scrollY with rAF throttling. Returns 0 when the user prefers
 * reduced motion so no transform is applied.
 */
function useScrollYWithReducedMotion(): number {
  const [y, setY] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches) {
      setY(0);
      return;
    }
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setY(window.scrollY);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return y;
}

const DEFAULT_VALUES = [
  { title: 'Handcrafted', description: 'Each piece is carefully crafted by skilled artisans with attention to every detail.', icon: 'sparkles' },
  { title: 'Sustainable', description: 'Ethically sourced materials and environmentally conscious practices.', icon: 'leaf' },
  { title: 'Timeless', description: 'Classic designs that transcend trends and become treasured heirlooms.', icon: 'gem' },
];

const CTA_STYLE_CLASS: Record<string, string> = {
  solid: 'bg-primary text-[var(--color-text-on-primary)] hover:bg-primary-hover hover:shadow-gold',
  'solid-gold': 'bg-secondary text-[var(--color-text-on-secondary)] hover:opacity-90 hover:shadow-gold font-semibold',
  outline: 'bg-transparent border-2 border-primary text-text-primary hover:bg-primary hover:text-[var(--color-text-on-primary)] hover:shadow-gold',
  'outline-gold': 'bg-transparent border-2 border-primary text-text-primary hover:bg-primary hover:text-[var(--color-text-on-primary)] hover:shadow-gold',
  ghost: 'bg-transparent text-text-primary hover:text-primary-hover',
};

const CTA_STYLE_CLASS_LIGHT: Record<string, string> = {
  solid: 'bg-primary text-[var(--color-text-on-primary)] hover:bg-primary-hover hover:shadow-gold',
  'solid-gold': 'bg-secondary text-[var(--color-text-on-secondary)] hover:opacity-90 hover:shadow-gold font-semibold',
  outline: 'bg-transparent border-2 border-primary text-[var(--color-text-on-primary)] hover:bg-primary hover:text-[var(--color-text-on-primary)]',
  'outline-gold': 'bg-transparent border-2 border-primary text-[var(--color-text-on-primary)] hover:bg-primary hover:text-[var(--color-text-on-primary)]',
  ghost: 'bg-transparent text-[var(--color-text-on-primary)] hover:text-primary-hover',
};

// Button typography is theme-driven. Stores with an editorial identity
// declare styling.buttonTextTransform + buttonLetterSpacing in branding.json.
// Stores without those fields get a sentence-case, normal-tracking button —
// avoiding accidental luxury-style inheritance.
const CTA_BASE_CLASS = 'inline-block px-2xl py-md no-underline rounded-sm text-sm font-medium transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

const HomePage: React.FC = () => {
  const { products, isLoading, error } = useProductsViewModel();
  const { topLevelCategories } = useCategoriesViewModel();
  const { branding } = useTheme();
  const localizedTo = useLocalizedTo();

  const featuredProducts = products.slice(0, 8);

  const heroConfig = branding?.hero;
  const showHero = heroConfig?.enabled !== false; // defaults to true
  const showSectionDivider = !!branding?.sectionTitles?.divider;
  const heroTitle = heroConfig?.title || 'Welcome to Our Store';
  const heroSubtitle = heroConfig?.subtitle || 'Discover amazing products';

  const heroCtaPrimary = heroConfig?.ctaPrimary || (heroConfig?.ctaText
    ? { text: heroConfig.ctaText, action: '/products', style: 'solid' as const }
    : null);
  const heroCtaSecondary = heroConfig?.ctaSecondary || null;

  const heroTextColor = heroConfig?.styling?.textColor || 'dark';
  const heroBackgroundImage = heroConfig?.backgroundImage;
  // Optional tiling background-pattern asset layered behind the hero
  // content. Any store can opt in via branding.hero.backgroundPatternAsset;
  // the asset itself is supplied per store-label in the whitelabel config.
  const heroBackgroundPatternAsset = heroConfig?.backgroundPatternAsset;

  const isLightHero = heroTextColor === 'light';

  // Button text-transform + tracking are driven by branding.hero.styling so
  // each store picks its own register: a store can declare uppercase +
  // letter-spacing for an editorial look, or leave these unset for
  // sentence-case.
  const buttonTextTransform = (heroConfig?.styling as { buttonTextTransform?: string } | undefined)
    ?.buttonTextTransform;
  const buttonLetterSpacing = (heroConfig?.styling as { buttonLetterSpacing?: string } | undefined)
    ?.buttonLetterSpacing;

  const ctaInlineStyle: React.CSSProperties = {
    fontFamily: 'var(--font-family-button, var(--font-family-primary))',
    textTransform: (buttonTextTransform as React.CSSProperties['textTransform']) || undefined,
    letterSpacing: buttonLetterSpacing,
  };

  const renderHeroCta = (
    cta: typeof heroCtaPrimary,
    variant: 'primary' | 'secondary',
  ) => {
    if (!cta) {
      return null;
    }

    const styleKey = (cta.style || (variant === 'primary' ? 'solid' : 'outline')) as keyof typeof CTA_STYLE_CLASS;
    const palette = isLightHero ? CTA_STYLE_CLASS_LIGHT : CTA_STYLE_CLASS;
    const styleClass =
      palette[styleKey] || palette[variant === 'primary' ? 'solid' : 'outline'];

    // Hero CTAs are store-config driven and may point at external URLs, so only
    // internal app paths get the active-locale prefix.
    const ctaHref = isInternalPath(cta.action) ? localizedTo(cta.action) : cta.action;

    return (
      <a
        key={`${variant}-${cta.text}`}
        href={ctaHref}
        target={cta.target || '_self'}
        rel={cta.target === '_blank' ? 'noreferrer' : undefined}
        className={cn(CTA_BASE_CLASS, styleClass)}
        style={ctaInlineStyle}
      >
        {cta.text}
      </a>
    );
  };

  const hasBackgroundImage = Boolean(heroBackgroundImage?.url);
  const markImage = heroConfig?.markImage;
  const isMarkCenterpiece =
    heroConfig?.variant === 'mark-centerpiece' && Boolean(markImage?.url);
  const isImmersive = hasBackgroundImage && !isMarkCenterpiece;

  const parallaxEnabled = heroConfig?.parallax?.enabled === true;
  const parallaxRatio = heroConfig?.parallax?.offsetRatio ?? 0.15;
  const scrollY = useScrollYWithReducedMotion();
  const parallaxOffset = parallaxEnabled ? Math.min(scrollY * parallaxRatio, 120) : 0;

  return (
    <div>
      {/* Hero — centerpiece variant: branded image foreground + stacked copy.
          Activated by any store that sets hero.variant = "mark-centerpiece" and
          provides hero.markImage in branding.json. Store-neutral rendering. */}
      {showHero && isMarkCenterpiece && (
        <section
          className="relative overflow-hidden"
          style={{
            // Full-bleed: escape any parent container padding/max-width so the
            // hero band reads as a deliberate editorial surface rather than
            // a floating rectangle on the page bg.
            width: '100vw',
            marginLeft: 'calc(50% - 50vw)',
            marginRight: 'calc(50% - 50vw)',
            // Surface color is declared by the store in branding.json. Stores
            // can set a fixed color (always-dark band regardless of theme) or
            // fall back to the current theme bg.
            backgroundColor: heroConfig?.styling?.surfaceColor || 'var(--color-bg)',
          }}
          data-testid="hero-mark-centerpiece"
        >
          <div className="flex flex-col items-center text-center pb-3xl md:pb-4xl">
            <img
              src={markImage!.url}
              alt={markImage!.alt || heroTitle}
              data-testid="hero-mark-image"
              className="w-full h-auto object-cover select-none pointer-events-none"
              style={{
                maxWidth: markImage!.widthDesktop || '100%',
                transform: parallaxOffset > 0 ? `translate3d(0, ${parallaxOffset}px, 0)` : undefined,
                willChange: parallaxEnabled ? 'transform' : undefined,
                transition: parallaxEnabled ? 'transform 40ms linear' : undefined,
              }}
              draggable={false}
            />
            {/* Hairline accent divider between banner and title */}
            <div
              aria-hidden="true"
              className="w-10 h-px mb-lg mt-xl"
              style={{ backgroundColor: 'var(--color-accent)' }}
            />
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl mb-md tracking-tight px-lg"
              style={{
                // Title color is declared by the store so it contrasts against
                // whatever surfaceColor the store chose for the hero band.
                color:
                  heroConfig?.styling?.titleColor || 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-heading)',
                fontWeight: heroConfig?.styling?.titleWeight ?? 300,
                letterSpacing:
                  heroConfig?.styling?.titleLetterSpacing || '-0.02em',
              }}
            >
              {heroTitle}
            </h1>
            <p
              className="text-base sm:text-lg mb-xl font-light leading-relaxed max-w-2xl px-lg"
              style={{
                color:
                  heroConfig?.styling?.subtitleColor ||
                  'var(--color-text-secondary)',
                fontFamily: 'var(--font-family-primary)',
              }}
            >
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-lg">
              {renderHeroCta(heroCtaPrimary, 'primary')}
              {renderHeroCta(heroCtaSecondary, 'secondary')}
            </div>
          </div>
          {/* Gradient seam: smooth transition from hero surface to page bg */}
          <div
            aria-hidden="true"
            className="absolute bottom-0 left-0 right-0 h-24"
            style={{
              background: `linear-gradient(to bottom, transparent, var(--color-bg))`,
            }}
          />
        </section>
      )}

      {/* Hero Section (immersive background-image and simple-centered variants) */}
      {showHero && !isMarkCenterpiece && <section
        className={cn('relative w-full overflow-hidden', isImmersive ? '' : 'bg-bg-secondary')}
        style={{
          minHeight: isImmersive ? '85vh' : undefined,
        }}
        data-testid="hero-section"
      >
        {/* Tiling background pattern (issue #143) — subtle grid behind the
            hero content. Rendered for any store that sets
            hero.backgroundPatternAsset. Sits below the z-10 content. */}
        {heroBackgroundPatternAsset && (
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: `url(${heroBackgroundPatternAsset})`,
              backgroundRepeat: 'repeat',
            }}
            aria-hidden="true"
            data-testid="hero-grid-pattern"
          />
        )}

        {/* Full-width background image (immersive variant only) */}
        {hasBackgroundImage && (
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: `url(${heroBackgroundImage!.url})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
            }}
            aria-hidden="true"
          />
        )}

        {/* Overlay for immersive variant only */}
        {isImmersive && (
          <div
            className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30"
            aria-hidden="true"
          />
        )}

        {/* Content */}
        {isImmersive ? (
          <div className="relative z-10 h-full min-h-[85vh] flex items-end pb-16 px-6 sm:px-12 lg:px-16">
            <div className="max-w-2xl">
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-light mb-4 tracking-tight"
                style={{
                  color: '#FFFFFF',
                  textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  fontFamily: 'var(--font-family-heading)',
                }}
              >
                {heroTitle}
              </h1>
              <p
                className="text-lg sm:text-xl mb-8 font-light"
                style={{
                  color: '#FFFFFF',
                  textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                  fontFamily: 'var(--font-family-primary)',
                }}
              >
                {heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {renderHeroCta(heroCtaPrimary, 'primary')}
                {renderHeroCta(heroCtaSecondary, 'secondary')}
              </div>
            </div>
          </div>
        ) : (
          <div
            className="relative z-10 py-4xl px-lg"
            style={{ maxWidth: heroConfig?.styling?.contentMaxWidth || '800px', margin: '0 auto' }}
          >
            <div className={cn('text-center')}>
              <h1
                className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light mb-lg tracking-tight"
                style={{
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-heading)',
                }}
              >
                {heroTitle}
              </h1>
              <p
                className="text-base sm:text-lg mb-2xl font-light leading-relaxed"
                style={{
                  color: 'var(--color-text-secondary)',
                  fontFamily: 'var(--font-family-primary)',
                }}
              >
                {heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {renderHeroCta(heroCtaPrimary, 'primary')}
                {renderHeroCta(heroCtaSecondary, 'secondary')}
              </div>
            </div>
          </div>
        )}
      </section>}

      {/* Featured Products */}
      <div className="container mx-auto px-lg py-3xl" style={{ maxWidth: 'var(--container-max-width)' }}>
        <div className="text-center mb-3xl">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-primary mb-md">
            Featured Products
          </h2>
          {showSectionDivider && (
            <span
              aria-hidden="true"
              className="block w-10 h-px mx-auto mt-3 mb-0"
              style={{ backgroundColor: 'var(--color-accent)' }}
              data-testid="section-divider"
            />
          )}
          <p className="text-text-secondary text-base max-w-xl mx-auto">
            Discover our handpicked selection of products
          </p>
        </div>

        <ProductGrid
          products={featuredProducts}
          isLoading={isLoading}
          error={error}
        />

        {featuredProducts.length > 0 && (
          <div className="text-center mt-3xl">
            <a
              href={localizedTo('/products')}
              className="inline-block px-2xl py-md bg-secondary text-[var(--color-text-on-secondary)] no-underline rounded-sm text-base font-semibold transition-all hover:opacity-90 hover:shadow-gold uppercase tracking-wider focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              View All Products
            </a>
          </div>
        )}
      </div>

      {/* Client Logos (B2B stores) */}
      {branding?.enterprise?.clientLogos !== undefined && (
        <ClientLogoStrip logos={branding.enterprise.clientLogos} />
      )}

      {/* Credentials bar. When a store opts into
          enterprise.credentialsBand.fullBleed the bar is wrapped in a
          full-bleed band that escapes the Layout's max-width container so the
          bg-bg-secondary surface reaches the viewport edges (same 100vw
          breakout the mark-centerpiece hero uses). */}
      {branding?.enterprise?.credentialsBand?.fullBleed ? (
        <FullBleedSection className="bg-bg-secondary" data-testid="credentials-band">
          <CredentialsBar credentials={branding?.enterprise?.credentials} />
        </FullBleedSection>
      ) : (
        <CredentialsBar credentials={branding?.enterprise?.credentials} />
      )}

      {/* Solutions by industry (B2B stores) */}
      {branding?.enterprise?.industries && branding.enterprise.industries.length > 0 && (
        <IndustryTabs industries={branding.enterprise.industries} />
      )}

      {/* Shop by Category — full-bleed band so the bg-bg-secondary surface
          reaches the viewport edges (was clipped to the Layout max-width). */}
      {topLevelCategories.length > 0 && (
        <FullBleedSection className="bg-bg-secondary border-t border-border-light">
          <div className="container mx-auto px-lg py-3xl" style={{ maxWidth: 'var(--container-max-width)' }}>
            <div className="text-center mb-2xl">
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-md">
                Shop by Category
              </h2>
              {showSectionDivider && (
                <span
                  aria-hidden="true"
                  className="block w-10 h-px mx-auto mt-3 mb-0"
                  style={{ backgroundColor: 'var(--color-accent)' }}
                  data-testid="section-divider"
                />
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-lg">
              {topLevelCategories.map((category) => (
                <a
                  key={category.id}
                  href={localizedTo(`/categories/${category.handle}`)}
                  className="group block bg-[var(--color-bg)] border border-border rounded-sm p-xl text-center no-underline transition-all hover:border-primary hover:shadow-md"
                >
                  <h3 className="text-lg font-medium text-text-primary group-hover:text-primary transition-colors">
                    {category.name}
                  </h3>
                  {category.description && (
                    <p className="text-sm text-text-secondary mt-2 line-clamp-2">
                      {category.description}
                    </p>
                  )}
                </a>
              ))}
            </div>
          </div>
        </FullBleedSection>
      )}

      {/* Values Section — driven by branding.values with sensible defaults */}
      {(() => {
        const values = branding?.values && branding.values.length > 0
          ? branding.values
          : DEFAULT_VALUES;
        const gridCols = values.length === 2 ? 'md:grid-cols-2'
          : values.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4'
          : 'md:grid-cols-3';
        return (
          <div className="bg-[var(--color-bg)] border-t border-border-light">
            <div className="container mx-auto px-lg py-3xl" style={{ maxWidth: 'var(--container-max-width)' }}>
              <div className={`grid grid-cols-1 ${gridCols} gap-2xl text-center`}>
                {values.map((value, idx) => (
                  <div key={`${value.title}-${idx}`} className="p-2xl flex flex-col items-center gap-sm">
                    {value.icon && (
                      <div
                        className="mb-sm text-primary"
                        aria-hidden="true"
                      >
                        <Icon name={value.icon} size={40} />
                      </div>
                    )}
                    <h3 className="font-serif text-2xl font-semibold text-text-primary">
                      {value.title}
                    </h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default HomePage;
